// AskBoardPanel — Ask Ember (board mode) embedded into /bod.
//
// Calls /api/ask/board, which:
//   - is gated to board_member+ at the worker
//   - uses the governance-tuned ask-board-system prompt (no internal
//     pipeline jargon; redirects operational questions to staff)
//   - restricts the retriever to bod_minutes / benchmark / kb_doc /
//     static_source / plan corpus kinds
//
// Differs from the staff-mode floating AskPanel in two ways: it lives
// inline on the BOD Brief page (no FAB, no drawer) and uses the board
// endpoint. Same streaming + citation + feedback contract underneath.

import { useState, useEffect, useRef, useCallback } from "react";
import {
  useAsk,
  fetchCitationByChunkId,
  type CitationDetails,
} from "../../hooks/useAI";

const SUGGESTED: string[] = [
  "How are we tracking against the Q1-2027 retention target?",
  "What governance decision should the board make next month given current viability bands?",
  "What does the latest national benchmark say about pipeline speed for departments our size?",
  "How does our application-to-BOD time compare to top-quartile departments?",
];

export default function AskBoardPanel() {
  const {
    ask,
    isStreaming,
    answer,
    confidence,
    model,
    error,
    queryId,
    citations,
  } = useAsk("/api/ask/board");
  const [question, setQuestion] = useState("");
  const [citationDetails, setCitationDetails] = useState<
    Record<string, CitationDetails>
  >({});
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Hydrate citation details as the streaming response references chunks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const c of citations) {
        const key = `${c.docIdPrefix}#${c.chunkIndex}`;
        if (citationDetails[key]) continue;
        try {
          const d = await fetchCitationByChunkId(c.docIdPrefix);
          if (!cancelled && d) setCitationDetails((p) => ({ ...p, [key]: d }));
        } catch {
          /* swallow — citation details are optional UX */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citations.length]);

  const submit = useCallback(() => {
    const q = question.trim();
    if (!q || isStreaming) return;
    ask(q, "bod-brief");
  }, [question, isStreaming, ask]);

  return (
    <section className="glass rounded-xl p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-base font-semibold text-theme-primary">
          Ask Ember (board mode)
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-theme-muted">
          Governance prompt · BOD-corpus only
        </span>
      </div>

      <p className="text-xs text-theme-muted mb-3">
        Same Ember, different filter. Restricted to BOD minutes, benchmarks,
        knowledge base, and strategic plans — no individual member or applicant
        data. Operational questions get redirected to staff.
      </p>

      {/* Suggested seeds when no question has been asked yet */}
      {!isStreaming && !answer && !error && (
        <div className="space-y-1.5 mb-3">
          {SUGGESTED.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setQuestion(s);
                inputRef.current?.focus();
              }}
              className="block w-full text-left text-xs px-3 py-1.5 rounded border border-theme-subtle text-theme-secondary hover:bg-theme-overlay/40 transition"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2 mb-3">
        <textarea
          ref={inputRef}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit();
          }}
          placeholder="Ask a governance question…"
          rows={2}
          className="flex-1 text-sm bg-theme-surface border border-theme-subtle rounded-lg px-3 py-2 text-theme-primary placeholder:text-theme-muted resize-none focus:outline-none focus:border-dcvfd-accent"
          disabled={isStreaming}
        />
        <button
          type="button"
          onClick={submit}
          disabled={isStreaming || !question.trim()}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            isStreaming || !question.trim()
              ? "bg-theme-overlay text-theme-muted cursor-not-allowed"
              : "bg-dcvfd text-white hover:bg-dcvfd-light"
          }`}
        >
          {isStreaming ? "Asking…" : "Ask"}
        </button>
      </div>

      {/* Output */}
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 px-4 py-3 text-sm mb-3">
          {error}
        </div>
      )}

      {(answer || isStreaming) && (
        <div className="rounded-lg border border-theme-subtle bg-theme-overlay/40 px-4 py-3">
          <div className="flex items-baseline gap-2 mb-2">
            {confidence && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-theme-overlay text-theme-secondary">
                Confidence: {confidence}
              </span>
            )}
            {model && (
              <span className="text-[10px] uppercase tracking-wider text-theme-muted font-mono">
                {model}
              </span>
            )}
          </div>
          <p className="text-sm text-theme-primary whitespace-pre-wrap leading-relaxed">
            {answer || (isStreaming ? "…" : "")}
          </p>
          {citations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-theme-subtle">
              <p className="text-xs text-theme-muted mb-1.5">Citations</p>
              <div className="flex flex-wrap gap-1.5">
                {citations.map((c) => {
                  const key = `${c.docIdPrefix}#${c.chunkIndex}`;
                  const d = citationDetails[key];
                  return (
                    <span
                      key={key}
                      title={
                        d
                          ? `${d.title ?? d.kind} · ${d.source_path ?? ""}`
                          : key
                      }
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-theme-subtle text-theme-secondary"
                    >
                      [{c.docIdPrefix}#{c.chunkIndex}]
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {queryId && (
            <p className="text-[10px] text-theme-muted mt-2 font-mono">
              query: {queryId}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
